import Logger from 'wanderu-logger';

import { createCheckoutForm } from './api';
import { createAppSlice } from './createAppSlice';

import type { CreateFormRequest } from './api';

const logger = Logger.getLogger('wanderu-orders:canopyCheckoutSlice');

// RD-6: bounds the Book-click order-creation call so a slow/hung orders
// service can never leave the traveler stuck -- FR-4 requires it resolve to
// a failure (and therefore the legacy fallback) within this window.
const CANOPY_CHECKOUT_TIMEOUT_MS = 5000;

interface CanopyCheckoutRejectedPayload {
    category: string;
    code: string;
    message: string;
}

// Deliberately separate from ordersSlice (RD-3): this order attempt never
// dispatches/reacts to orderIdReceived, so the discount/detailedPricing
// listener-middleware side effects in listeners.ts never fire for it.
type CanopyCheckoutState =
    | { orderId: null; status: 'idle' }
    | { orderId: null; status: 'pending' }
    | { orderId: string; status: 'succeeded' }
    | { orderId: null; status: 'failed' };

const initialState: CanopyCheckoutState = { orderId: null, status: 'idle' };

/**
 * Extracts a structured checkout error from an unknown thrown value. The
 * checkout-v1 error envelope (CheckoutError) is the full response body
 * ({code, category, details}) -- unlike the legacy orders API's nested
 * response.error shape (see errors.ts / parseApiError).
 */
function parseCheckoutError(error: unknown): CanopyCheckoutRejectedPayload {
    const responseBody = error != null && typeof error === 'object' && 'responseBody' in error
        ? (error as Record<string, unknown>).responseBody as Record<string, unknown> | undefined
        : undefined;
    const code = typeof responseBody?.code === 'string'
        ? responseBody.code
        : 'UNKNOWN';
    const category = typeof responseBody?.category === 'string'
        ? responseBody.category
        : 'SYSTEM_ERROR';
    const message = error instanceof Error
        ? error.message
        : String(error);
    return { category, code, message };
}

const canopyCheckoutSlice = createAppSlice({
    initialState,
    name: 'canopyCheckout',
    reducers: (create) => ({
        canopyCheckoutReset: create.reducer(() => initialState),
        requestCanopyCheckoutOrder: create.asyncThunk(
            async (request: CreateFormRequest, { rejectWithValue }) => {
                const controller = new AbortController();
                const timeoutId = setTimeout(
                    () => controller.abort(),
                    CANOPY_CHECKOUT_TIMEOUT_MS,
                );
                try {
                    const requestOpts = { signal: controller.signal };
                    const { orderId } = await createCheckoutForm(request, requestOpts);
                    logger.info('requestCanopyCheckoutOrder', { ...request, orderId });
                    return { orderId };
                } catch (error: unknown) {
                    // A hung request only ever aborts via this thunk's own
                    // timeout (RD-6) -- no other caller holds the controller
                    // -- so distinguish it from a real backend error/network
                    // failure rather than logging it as an opaque UNKNOWN.
                    const parsed = controller.signal.aborted
                        ? { category: 'SYSTEM_ERROR', code: 'CLIENT_TIMEOUT', message: 'requestCanopyCheckoutOrder timed out' }
                        : parseCheckoutError(error);
                    logger.error('requestCanopyCheckoutOrder', { ...request, ...parsed });
                    return rejectWithValue(parsed);
                } finally {
                    clearTimeout(timeoutId);
                }
            },
            {
                fulfilled: (_state, action) => ({
                    orderId: action.payload.orderId,
                    status: 'succeeded',
                }) satisfies CanopyCheckoutState,
                pending: () => ({
                    orderId: null,
                    status: 'pending',
                }) satisfies CanopyCheckoutState,
                rejected: () => ({
                    orderId: null,
                    status: 'failed',
                }) satisfies CanopyCheckoutState,
            },
        ),
    }),
    selectors: {
        selectCanopyCheckoutOrderId: (state) => state.orderId,
        selectCanopyCheckoutStatus: (state) => state.status,
    },
});

export type { CanopyCheckoutRejectedPayload, CanopyCheckoutState };
export { CANOPY_CHECKOUT_TIMEOUT_MS, parseCheckoutError };
export const { canopyCheckoutReset, requestCanopyCheckoutOrder } = canopyCheckoutSlice.actions;
export const canopyCheckoutReducer = canopyCheckoutSlice.reducer;
export const getCanopyCheckoutSelectors = canopyCheckoutSlice.getSelectors;
export const {
    selectCanopyCheckoutOrderId,
    selectCanopyCheckoutStatus,
} = canopyCheckoutSlice.selectors;
